import * as React from 'react'
import BoringAvatar from 'boring-avatars'

import { getIdenticonProps } from '@/lib/identicon/config'
import { cn } from '@/lib/utils'

export interface IdenticonProps {
  /** Stable seed (e.g. profile id, user id, email). */
  seed: string
  className?: string
  /** When true, hide from assistive tech (name shown nearby). */
  decorative?: boolean
}

/**
 * Deterministic geometric identicon (boring-avatars bauhaus + retro palette).
 * Sizes to its container via ResizeObserver.
 */
export function Identicon({ seed, className, decorative = true }: IdenticonProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [size, setSize] = React.useState(40)

  React.useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    const update = () => {
      const rect = el.getBoundingClientRect()
      setSize(Math.max(1, Math.round(Math.min(rect.width, rect.height))))
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const props = React.useMemo(() => getIdenticonProps(seed, size), [seed, size])

  return (
    <div
      ref={containerRef}
      className={cn('size-full overflow-hidden', className)}
      aria-hidden={decorative || undefined}
    >
      <BoringAvatar {...props} className="size-full" />
    </div>
  )
}
